import { useEffect, useEffectEvent, useRef } from "react";
import type { ThreeDSStatus } from "@fluid-app/fluid-pay-core/types";
import { belongsToActiveAttempt } from "./three-ds-attempt";

export interface Vgs3DSCompletionResult {
  success: boolean;
  transactionId: string;
  status?: string;
}

export interface Vgs3DSChallengeEvent {
  xid: string;
  /** Optional for backward compatibility: older events may omit it, and the
   *  attempt-scoping guard deliberately honors key-less events. */
  merchant_transaction_id?: string;
  status: "success" | "failed";
  challenge_status?: string;
  error_message?: string;
}

/** Minimal structural slice of a realtime channel — pusher-js's `Channel`
 *  satisfies this as-is (its `bind`/`unbind` return `Channel`, which is
 *  assignable to a `void`-returning method). */
export interface ThreeDsRealtimeChannel {
  bind(eventName: string, handler: (data: Vgs3DSChallengeEvent) => void): void;
  unbind(
    eventName: string,
    handler: (data: Vgs3DSChallengeEvent) => void,
  ): void;
}

/** Transport abstraction for the fast path — the app's `pusherClient`
 *  satisfies this structurally with no adapter. */
export interface ThreeDsRealtimeTransport {
  subscribe(channelName: string): ThreeDsRealtimeChannel | null;
  unsubscribe(channelName: string): void;
}

interface UseVgs3DSCompletionOptions {
  /** Realtime channel carrying the `vgs_3ds_challenge` event
   *  (e.g. `private-cart-{cartToken}`). */
  channelName: string;
  transport: ThreeDsRealtimeTransport;
  /** Status poll — the reliable fallback alongside the realtime fast path. */
  checkStatus: () => Promise<ThreeDSStatus | null>;
  onComplete: (result: Vgs3DSCompletionResult) => void;
  onError: (error: string) => void;
  /** Subscribe to the realtime channel and start polling when true (i.e. when the 3DS modal is open). */
  enabled?: boolean;
  /** The active attempt's merchant_tx_id — when present, completion signals
   *  for any OTHER attempt are ignored (stale metadata, late webhooks). */
  merchantTxId?: string;
  /** Called when the attempt was canceled (e.g. from another tab) — a quiet,
   *  no-error terminal. */
  onCanceled?: () => void;
}

// Poll interval and timeout for the status-poll fallback.
// Polling at 3-second intervals is the reliable secondary check alongside
// the Pusher fast path (the backend broadcasts the result immediately).
// The 6.5-minute timeout sits past the backend safety-net boundary (~6:00),
// so the poll surfaces the backend's verdict — accurate decline, rescued
// approval, or TIMEOUT — instead of the frontend self-declaring first.
const POLL_INTERVAL_MS = 3_000;
const TIMEOUT_MS = 390_000;

/**
 * Subscribes to the realtime transport and polls the injected status check
 * for the 3DS challenge result.
 *
 * Activated by `enabled` — pass `vgsThreeDSModal.isOpen` so the subscription
 * starts as soon as the challenge modal opens. No URL-param dance needed.
 */
export function useVgs3DSCompletion({
  channelName,
  transport,
  checkStatus,
  onComplete,
  onError,
  enabled = false,
  merchantTxId,
  onCanceled,
}: UseVgs3DSCompletionOptions) {
  const hasHandledRef = useRef(false);
  const isSubscribedRef = useRef(false);

  // Kept current via a per-render effect so an inline `transport` value at
  // the call site never retriggers the main effect.
  const transportRef = useRef(transport);
  useEffect(() => {
    transportRef.current = transport;
  });

  const handleComplete = useEffectEvent((result: Vgs3DSCompletionResult) => {
    onComplete(result);
  });

  const handleError = useEffectEvent((error: string) => {
    onError(error);
  });

  const handleCanceled = useEffectEvent(() => {
    onCanceled?.();
  });

  // Non-reactive wrapper so an inline `checkStatus` closure at the call site
  // never retriggers the main effect. Called from the poll during the active
  // phase only — never from cleanup.
  const performStatusCheck = useEffectEvent(() => checkStatus());

  // Shared handler for Pusher events, polling results, and timeout.
  // hasHandledRef ensures only the first source to resolve wins.
  const handleResult = useEffectEvent(
    (result: {
      status: "success" | "failed";
      errorMessage?: string;
      transactionId?: string;
    }) => {
      if (hasHandledRef.current) return;
      hasHandledRef.current = true;

      if (result.status === "success") {
        handleComplete({
          success: true,
          transactionId: result.transactionId ?? "",
          status: result.status,
        });
      } else {
        handleError(result.errorMessage || "3DS verification failed");
      }
    },
  );

  // Reset handled flag when enabled toggles off (modal closed / new attempt).
  useEffect(() => {
    if (!enabled && hasHandledRef.current) {
      hasHandledRef.current = false;
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !channelName || hasHandledRef.current) {
      return;
    }

    if (isSubscribedRef.current) {
      return;
    }

    let cancelled = false;
    // Captured ONCE so subscribe and the cleanup's unsubscribe use the same
    // transport object even if a different one is passed mid-flight.
    const activeTransport = transportRef.current;

    isSubscribedRef.current = true;
    const channel = activeTransport.subscribe(channelName);
    const hasPusher = !!channel;

    if (!hasPusher) {
      console.error(
        "[VGS 3DS] Pusher subscribe returned null — using status polling only",
      );
      isSubscribedRef.current = false;
      // intentionally NOT returning — polling + timeout below are the fallback
    }

    // --- Fast path: Pusher event ---
    const handleVgs3DSChallenge = (data: Vgs3DSChallengeEvent) => {
      if (cancelled) return;

      // Attempt-scoping: ignore events for a different (older) attempt.
      if (!belongsToActiveAttempt(merchantTxId, data.merchant_transaction_id)) {
        return;
      }

      handleResult({
        status: data.status,
        errorMessage: data.error_message,
        transactionId: data.merchant_transaction_id,
      });
    };

    if (hasPusher) {
      channel.bind("vgs_3ds_challenge", handleVgs3DSChallenge);
    }

    // --- Reliable fallback: poll the injected status check ---
    const pollInterval = setInterval(async () => {
      if (hasHandledRef.current || cancelled) return;

      const status = await performStatusCheck();
      if (cancelled || !status) return;

      // Attempt-scoping: stale metadata from a previous attempt must not
      // resolve this modal (the backend wipes per-attempt keys on re-init,
      // but a late webhook can still land mid-retry).
      if (!belongsToActiveAttempt(merchantTxId, status.merchantTxId)) {
        return;
      }

      if (status.completed) {
        handleResult({ status: "success" });
      } else if (status.failed) {
        handleResult({
          status: "failed",
          errorMessage:
            status.errorMessage ||
            `3DS verification ${status.status || "failed"}`,
        });
      } else if (status.canceled) {
        // Canceled (possibly from another tab): quiet no-error terminal.
        if (!hasHandledRef.current) {
          hasHandledRef.current = true;
          handleCanceled();
        }
      }
    }, POLL_INTERVAL_MS);

    // --- Safety net: hard timeout ---
    const timeout = setTimeout(() => {
      if (hasHandledRef.current || cancelled) return;

      console.error("[VGS 3DS] Timed out waiting for 3DS result");
      handleResult({
        status: "failed",
        errorMessage: "Payment verification timed out. Please try again.",
      });
    }, TIMEOUT_MS);

    // Cleanup — cancelled flag prevents in-flight polls from calling handleResult
    return () => {
      cancelled = true;
      clearTimeout(timeout);
      clearInterval(pollInterval);
      if (hasPusher) {
        channel.unbind("vgs_3ds_challenge", handleVgs3DSChallenge);
        activeTransport.unsubscribe(channelName);
      }
      isSubscribedRef.current = false;
    };
  }, [enabled, channelName, merchantTxId]);
}
